/**
 * Image Prompt Input Component - Glass Morphism Design
 * Text input for image generation with prompt enhancer toggle
 * Validates: Requirements 16.1, 16.2, 18.1
 */
import { useState, useRef, useEffect } from 'react';
import { useImageStore } from '../../stores/imageStore';
import { Icons } from '../icons';
import { THEME } from '../../styles/theme';

interface ImagePromptInputProps {
  onSubmit: (prompt: string) => void;
  disabled?: boolean;
}

export function ImagePromptInput({ onSubmit, disabled }: ImagePromptInputProps) {
  const [prompt, setPrompt] = useState('');
  const textareaRef = useRef<HTMLTextAreaElement>(null);

  const {
    promptEnhancerEnabled,
    togglePromptEnhancer,
  } = useImageStore();

  // Auto-resize textarea
  useEffect(() => {
    if (textareaRef.current) {
      textareaRef.current.style.height = 'auto';
      textareaRef.current.style.height = `${Math.min(textareaRef.current.scrollHeight, 200)}px`;
    }
  }, [prompt]);

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    if (prompt.trim() && !disabled) {
      onSubmit(prompt.trim());
    }
  };

  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === 'Enter' && (e.metaKey || e.ctrlKey)) {
      handleSubmit(e);
    }
  };

  return (
    <form onSubmit={handleSubmit}>
      {/* Input Container */}
      <div
        className="rounded-2xl p-4 border relative overflow-hidden"
        style={{
          backgroundColor: THEME.glassLight,
          backdropFilter: 'blur(10px)',
          borderColor: THEME.border,
          boxShadow: THEME.shadowLg,
        }}
      >
        {/* Dot pattern overlay */}
        <div
          className="absolute inset-0 pointer-events-none"
          style={{
            backgroundImage: 'radial-gradient(rgba(255,255,255,0.02) 1px, transparent 1px)',
            backgroundSize: '12px 12px',
          }}
        />

        {/* Prompt Input */}
        <div className="relative">
          <textarea
            ref={textareaRef}
            value={prompt}
            onChange={(e) => setPrompt(e.target.value)}
            onKeyDown={handleKeyDown}
            disabled={disabled}
            placeholder="Describe your image... Be specific about style, colors, composition, and details."
            className="w-full bg-transparent resize-none border-0 focus:outline-none disabled:opacity-50 min-h-[100px] p-0"
            rows={3}
            style={{
              fontSize: '15px',
              lineHeight: '1.6',
              color: THEME.textMain,
            }}
          />
        </div>

        {/* Actions Row */}
        <div className="flex items-center justify-between mt-4 relative">
          {/* Prompt Enhancer Toggle */}
          <button
            type="button"
            onClick={togglePromptEnhancer}
            disabled={disabled || !prompt.trim()}
            className={`flex items-center gap-2 px-4 py-2 rounded-xl text-sm font-medium border transition-all duration-200 ${
              promptEnhancerEnabled ? 'animate-pulse-glow' : ''
            } disabled:opacity-50`}
            style={{
              borderColor: prompt.trim()
                ? promptEnhancerEnabled
                  ? THEME.accentBorder
                  : THEME.border
                : THEME.border,
              backgroundColor: prompt.trim()
                ? promptEnhancerEnabled
                  ? THEME.accentSoft
                  : 'transparent'
                : 'transparent',
              color: prompt.trim()
                ? promptEnhancerEnabled
                  ? THEME.accentColor
                  : THEME.textMuted
                : THEME.textMuted,
            }}
          >
            <Icons.Sparkles size={16} />
            <span>Enhance</span>
          </button>

          {/* Submit Button */}
          <button
            type="submit"
            disabled={disabled || !prompt.trim()}
            className="flex items-center gap-2 px-5 py-2.5 rounded-xl font-medium transition-all duration-200 disabled:opacity-50 disabled:cursor-not-allowed"
            style={{
              background: prompt.trim() ? THEME.accent : 'rgba(255,255,255,0.05)',
              color: THEME.textMain,
              boxShadow: prompt.trim() ? THEME.shadowAccent : 'none',
            }}
          >
            <Icons.Image size={18} />
            <span>Generate</span>
          </button>
        </div>

        {/* Keyboard Shortcut Hint */}
        <p className="mt-3 text-xs text-right relative" style={{ color: THEME.textMuted }}>
          Press ⌘+Enter to generate
        </p>
      </div>
    </form>
  );
}
